/**
 * The two reads this surface makes, each behind a stable callback so the resource hook does not
 * restart on every render.
 *
 * TWO INDEPENDENT QUERIES, NOT ONE COMBINED ONE. The page renders the five static cards
 * immediately and lets only the terms card and the contact block resolve — "the page must never be
 * blank waiting on a help page". Combining them would make the whole page wait for the slower of
 * the two, and would make a 403 on the contacts read take the terms card down with it.
 */
import { useCallback } from 'react';
import type { AgreementList, PlatformContacts, TermsState } from '../api/contract';
import { getCurrentTerms, getPlatformContacts, listAgreements } from '../api/operations';
import { useApiTransport } from '../api/transport';
import { listFromTerms } from '../agreements';
import { useAsyncResource } from './use-async-resource';
import type { AsyncResource } from './use-async-resource';

export function usePlatformContacts(): AsyncResource<PlatformContacts> {
  const transport = useApiTransport();
  const read = useCallback(
    (signal: AbortSignal) => getPlatformContacts(transport, { signal }),
    [transport],
  );
  return useAsyncResource(read);
}

export function useCurrentTerms(): AsyncResource<TermsState> {
  const transport = useApiTransport();
  const read = useCallback(
    (signal: AbortSignal) => getCurrentTerms(transport, { signal }),
    [transport],
  );
  return useAsyncResource(read);
}

/** `listAgreements` (design 25): every document and what this author accepted - Settings' list. */
export function useAgreements(): AsyncResource<AgreementList> {
  const transport = useApiTransport();
  const read = useCallback(
    (signal: AbortSignal) => listAgreements(transport, { signal }),
    [transport],
  );
  return useAsyncResource(read);
}

/** Where the interstitial's list came from, which decides how its acceptance is sent. */
export interface AgreementsToAccept {
  readonly list: AgreementList;
  /** `agreements`: `acceptAgreements`; `terms`: the author agreement alone, through `acceptTerms`. */
  readonly via: 'agreements' | 'terms';
}

/**
 * The interstitial's read: `listAgreements`, or - when it cannot be read - the author agreement's
 * own read standing in for it (../agreements.ts). A failure of that one too is the screen's failure.
 */
export function useAgreementsToAccept(): AsyncResource<AgreementsToAccept> {
  const transport = useApiTransport();
  const read = useCallback(
    async (signal: AbortSignal): Promise<AgreementsToAccept> => {
      try {
        return { list: await listAgreements(transport, { signal }), via: 'agreements' };
      } catch (thrown) {
        if (signal.aborted) {
          throw thrown;
        }
        const terms = await getCurrentTerms(transport, { signal });
        return { list: listFromTerms(terms), via: 'terms' };
      }
    },
    [transport],
  );
  return useAsyncResource(read);
}
